import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { ProcessingOverlay } from "../ui/ProcessingOverlay";
import { SearchSelect } from "../ui/SearchSelect";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";

// tRPC input tipi (create) — payload'ı bir kez kurup gerekirse çift onayla tekrar göndermek için.
type CreatePayload = Parameters<ReturnType<typeof trpc.productionBatch.create.useMutation>["mutateAsync"]>[0];

interface BypRow { key: string; sel: string; ratio: string; qty: string; manual: boolean }

interface Props {
  open: boolean;
  orderId?: string | null;
  orderCode?: string;
  /** İş emri bağlamı (merkez kuyruğundan açılınca). */
  woId?: string;
  woCode?: string;
  /** Verilirse hat seçimi bu merkezin altındaki hatlara filtrelenir. */
  centerId?: string | null;
  itemKind: "semi" | "finished";
  itemId: string;
  itemLabel: string;
  uom?: string;
  /** Sipariş/iş emri miktarı — bilgilendirme için gösterilir. */
  orderedQty?: number;
  producedQty?: number;
  onClose: () => void;
  onCreated?: (code: string, warnings?: string[], count?: number, produced?: string[], orderReady?: boolean, byproducts?: Array<{ code: string; lotCode: string }>) => void;
}

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}

/**
 * Cascade drawer — bir sipariş satırı için parti üretim kaydı oluşturur.
 * Üretim hattı + miktar girilir; kaydet → URT kodu + çıktı lotu (stoğa giriş).
 */
export function ProductionBatchDrawer({
  open, orderId, orderCode, woId, woCode, centerId, itemKind, itemId, itemLabel, uom, orderedQty, producedQty, onClose, onCreated,
}: Props) {
  const qc = useQueryClient();
  const [locationId, setLocationId] = useState("");
  const [qty, setQty] = useState("");
  const [count, setCount] = useState("1");
  const [notes, setNotes] = useState("");
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  // Yan ürünler (co-production): kullanıcı istediği kadar ekler; miktar ana miktara oranlı (elle düzeltilebilir).
  const [byproducts, setByproducts] = useState<BypRow[]>([]);
  const bypKey = useRef(0);
  const bypPrefilled = useRef<string>(""); // reçete yan ürünlerini ürün başına bir kez ön-doldur (kullanıcı düzenlemesini ezme)

  const locationsQ = trpc.location.list.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const produciblesQ = trpc.recipe.producibles.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  // Üretilen miktar varsayılanı = ürünün aktif reçetesinin batch boyutu (kullanıcı değiştirebilir).
  const batchSizeQ = trpc.recipe.batchSizeFor.useQuery({ kind: itemKind, id: itemId }, { enabled: open, staleTime: 60_000 });
  // Reçeteye bağlı yan ürünler (co-production) — üretim ekranında "Yan Ürünler"i otomatik doldurmak için (oran + kind/id).
  const recipeBypQ = trpc.recipe.activeByproductsFor.useQuery({ kind: itemKind, id: itemId }, { enabled: open, staleTime: 60_000 });
  /**
   * TÜKETİM ÖNİZLEMESİ — "kaydedersem ne düşecek?"
   *
   * Parti oluşturmak reçete bileşenlerini FEFO ile tüketir. Bu SESSİZ olduğu için, mamul
   * stoğunu (sayım/açılış) yanlışlıkla buradan giren kullanıcı hammaddeyi eksiltir ve
   * durumu ancak sonradan fark eder. Miktar girilir girilmez ne tükeneceği gösterilir.
   */
  const qtyNum = Number(qty);
  const previewQty = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 0;
  const tuketimQ = trpc.recipe.consumptionPreview.useQuery(
    { kind: itemKind, id: itemId, qty: previewQty || 1 },
    { enabled: open && !!itemId, staleTime: 30_000 },
  );
  // Merkez parti parametreleri (yalnız merkez bağlamında, aktif olanlar).
  const centerParamsQ = trpc.productionCenterParam.list.useQuery(
    { centerId: centerId ?? "", activeOnly: true },
    { enabled: open && !!centerId, staleTime: 30_000 },
  );
  const centerParams = (centerParamsQ.data ?? []) as Array<{
    id: string; label: string; dataType: "number" | "text" | "select" | "boolean" | "date";
    unit: string | null; options: string[]; required: boolean; minVal: number | null; maxVal: number | null;
  }>;
  const createMut = trpc.productionBatch.create.useMutation();
  // İş emrine atanmış TÜM hatlar (çoklu hat destekli) → üretim yalnız bunlardan birine girilebilir.
  const linesForWoQ = trpc.lineStatus.linesForWo.useQuery({ woId: woId ?? "" }, { enabled: open && !!woId, staleTime: 5_000 });
  // Meşgul hatta iş emri geçişi çift onayı (SWITCH_CONFIRM) — onaylanınca payload confirmLineSwitch ile tekrar.
  const [switchPrompt, setSwitchPrompt] = useState<{ message: string; payload: CreatePayload } | null>(null);
  // ÇOKLU PARTİ ikinci onayı — sunucu count>1 iken confirmMultiBatch bekler (guard atlanamasın diye
  // arayüzde değil sunucuda). Kullanıcı TOPLAM miktarı görüp ayrıca onaylar.
  const [multiPrompt, setMultiPrompt] = useState<{ message: string; payload: CreatePayload } | null>(null);

  // Yan ürün seçenekleri: üretilebilir (semi/finished) — ana ürün hariç.
  const bypOptions = useMemo(
    () => ((produciblesQ.data ?? []) as Array<{ kind: string; id: string; code: string; name: string; baseUomSymbol: string | null }>)
      .filter((p) => !(p.kind === itemKind && p.id === itemId))
      .map((p) => ({ value: `${p.kind}:${p.id}`, label: `${p.code} — ${p.name}`, tail: p.baseUomSymbol ?? undefined })),
    [produciblesQ.data, itemKind, itemId],
  );
  const bypUom = (sel: string) => ((produciblesQ.data ?? []) as Array<{ kind: string; id: string; baseUomSymbol: string | null }>).find((p) => `${p.kind}:${p.id}` === sel)?.baseUomSymbol ?? "";
  const num = (s: string) => Number((s || "").replace(",", "."));

  // Drawer açılınca / ürün değişince: reçetedeki yan ürünleri (varsa) orana göre ön-doldur (ürün başına 1 kez).
  // Kullanıcı sonra düzenler/kaldırır/ekler; ana miktar değişince oranlı satırlar aşağıdaki effect ile yeniden ölçeklenir.
  useEffect(() => {
    if (!open) { bypPrefilled.current = ""; setByproducts([]); return; }
    if (bypPrefilled.current === itemId) return; // bu ürün için zaten dolduruldu — kullanıcı düzenlemesini ezme
    if (!recipeBypQ.data) return; // reçete yan ürünleri gelene kadar bekle
    bypPrefilled.current = itemId;
    const mq = num(qty);
    setByproducts(recipeBypQ.data.map((bp) => {
      const rt = bp.ratio;
      return {
        key: String(++bypKey.current),
        sel: `${bp.itemKind}:${bp.itemId}`,
        ratio: String(rt),
        qty: (mq > 0 && rt > 0) ? String(Math.round(mq * rt * 1e6) / 1e6) : "",
        manual: false,
      };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemId, recipeBypQ.data]);
  // Ana miktar değişince: elle düzeltilmemiş yan ürün miktarlarını orana göre yeniden hesapla.
  useEffect(() => {
    const mq = num(qty);
    setByproducts((rows) => rows.map((r) => {
      if (r.manual) return r;
      const rt = num(r.ratio);
      return (mq > 0 && rt > 0) ? { ...r, qty: String(Math.round(mq * rt * 1e6) / 1e6) } : r;
    }));
  }, [qty]);

  function addByp() { setByproducts((r) => [...r, { key: String(++bypKey.current), sel: "", ratio: "", qty: "", manual: false }]); }
  function patchByp(key: string, patch: Partial<BypRow>) { setByproducts((rows) => rows.map((r) => r.key === key ? { ...r, ...patch } : r)); }
  function setBypRatio(key: string, ratio: string) {
    const mq = num(qty); const rt = num(ratio);
    patchByp(key, { ratio, manual: false, qty: (mq > 0 && rt > 0) ? String(Math.round(mq * rt * 1e6) / 1e6) : "" });
  }

  // Merkez verildiyse hat seçimini o merkezin altındaki aktif hatlara filtrele.
  const lines = useMemo(
    () => ((locationsQ.data ?? []) as Array<{ id: string; type: string; isActive: boolean; parentId: string | null; code: string; name: string }>)
      .filter((l) => l.type === "uretim_hatti" && l.isActive && (!centerId || l.parentId === centerId)),
    [locationsQ.data, centerId],
  );

  // WO-bağlı üretimde hat, iş emrinin ATANDIĞI hatlarla SINIRLI (Hat Yönetimi'nden atanır → karmaşayı önle).
  // Çoklu hatta atanmışsa kullanıcı birini SEÇER; tek hatta atanmışsa kilitli; atama yoksa üretim engellenir.
  const woAssignedLines = woId ? (linesForWoQ.data ?? []) : [];
  const woUnassigned = !!woId && linesForWoQ.isSuccess && woAssignedLines.length === 0;

  /**
   * ÖRNEK ÜRÜN RESMİ KAPISI — sunucu da reddediyor; burası kullanıcıyı KAYDETMEDEN ÖNCE uyarır.
   * Eşik (`gerekli`) UÇTAN gelir, burada "3" YAZILMAZ — ayrışırsa ekran "hazır" der, sunucu reddeder.
   */
  const gateQ = trpc.productionBatch.sampleGate.useQuery(
    { woId: woId ?? "" },
    { enabled: open && !!woId && itemKind === "finished", staleTime: 10_000 },
  );
  const resimEksik = !!woId && itemKind === "finished" && gateQ.isSuccess && !gateQ.data.uygun;

  useEffect(() => {
    if (!open) return;
    setLocationId("");
    setQty("");
    setCount("1");
    setNotes("");
    setParamValues({});
    setErr(null);
    createMut.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemId]);

  // Reçete batch boyutu gelince, kullanıcı henüz girmediyse "üretilen miktar"ı varsayılan doldur.
  useEffect(() => {
    const bs = batchSizeQ.data?.batchSize;
    if (open && bs != null) setQty((prev) => (prev === "" ? String(bs) : prev));
  }, [open, batchSizeQ.data]);

  // Hat seçimi: WO-bağlıysa YALNIZ iş emrinin atandığı hatlar (varsayılan = en son atanan). WO yoksa tek hatta oto-seç.
  useEffect(() => {
    if (!open || locationId) return;
    if (woId) { if (woAssignedLines.length) setLocationId(woAssignedLines[0]!.id); return; } // atanmamışsa boş kalır → üretim engelli
    if (lines.length === 1) setLocationId(lines[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lines, linesForWoQ.data]);

  // SWITCH_CONFIRM ConfirmDialog ile ele alınır → ham mesajı banner'da gösterme.
  const rawMutErr = createMut.error?.message ?? null;
  const mutErr = rawMutErr && rawMutErr.includes("SWITCH_CONFIRM") ? null : rawMutErr;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (woUnassigned) return setErr("Bu iş emri bir üretim hattına atanmamış — önce Hat Yönetimi'nden iş emrini bir hatta atayın.");
    if (resimEksik) return setErr(`Bu iş emri için en az ${gateQ.data!.gerekli} örnek ürün resmi gerekiyor (şu an ${gateQ.data!.adet}). İş emri detayından «Ürün Resimleri» bölümünde kategoriyi «Örnek Ürün» seçip ${gateQ.data!.eksik} resim daha yükleyin.`);
    if (!locationId) return setErr("Üretim hattı seçin.");
    const n = Number(qty);
    if (!qty || !(n > 0)) return setErr("Geçerli bir üretim miktarı girin.");
    const cnt = Number(count);
    if (!count || !Number.isFinite(cnt) || !Number.isInteger(cnt) || cnt < 1) return setErr("Parti sayısı en az 1 (tam sayı) olmalı.");
    if (cnt > 100) return setErr("En fazla 100 parti oluşturulabilir.");

    // Merkez parametreleri: zorunlu doğrulama + sayı aralığı + payload.
    const params: Array<{ paramId: string; value: string }> = [];
    for (const p of centerParams) {
      const raw = (paramValues[p.id] ?? "").trim();
      if (!raw) {
        if (p.required) return setErr(`"${p.label}" parametresi zorunlu.`);
        continue;
      }
      if (p.dataType === "number") {
        const v = Number(raw.replace(",", "."));
        if (!Number.isFinite(v)) return setErr(`"${p.label}" geçerli bir sayı olmalı.`);
        if (p.minVal != null && v < p.minVal) return setErr(`"${p.label}" en az ${p.minVal} olmalı.`);
        if (p.maxVal != null && v > p.maxVal) return setErr(`"${p.label}" en fazla ${p.maxVal} olmalı.`);
        params.push({ paramId: p.id, value: String(v) });
      } else {
        params.push({ paramId: p.id, value: raw });
      }
    }

    // Yan ürünler (co-production): tek parti + geçerli ürün+miktar.
    const byps: Array<{ itemKind: "semi" | "finished"; itemId: string; qty: number }> = [];
    if (byproducts.length) {
      if (cnt !== 1) return setErr("Yan ürünlü üretim tek parti olmalı (Parti Sayısı = 1).");
      for (const r of byproducts) {
        if (!r.sel) return setErr("Yan ürün seçin veya satırı kaldırın.");
        const [k, id] = r.sel.split(":");
        const q = num(r.qty);
        if (!(q > 0)) return setErr("Her yan ürün için 0'dan büyük miktar girin.");
        byps.push({ itemKind: k as "semi" | "finished", itemId: id!, qty: q });
      }
    }

    const payload: CreatePayload = {
      orderId: orderId ?? null,
      woId: woId ?? null,
      itemKind,
      itemId,
      locationId,
      qty: n,
      count: cnt,
      notes: notes.trim() || undefined,
      params: params.length ? params : undefined,
      byproducts: byps.length ? byps : undefined,
    };
    await runCreate(payload);
  }

  // Create'i çalıştır; meşgul hatta iş emri geçişi gerekiyorsa (SWITCH_CONFIRM) çift onay iste, onaylanınca tekrar dene.
  async function runCreate(payload: CreatePayload) {
    try {
      const res = await createMut.mutateAsync(payload);
      await qc.invalidateQueries();
      onCreated?.(res.code, res.warnings, res.count, res.produced, res.orderReady, res.byproducts);
      setSwitchPrompt(null);
      setMultiPrompt(null);
      onClose();
    } catch (e) {
      const msg = (e as Error)?.message ?? "";
      if (msg.includes("SWITCH_CONFIRM")) {
        setSwitchPrompt({ message: msg.replace(/^SWITCH_CONFIRM:\s*/, ""), payload });
      } else if (msg.includes("ÇOKLU_PARTI_ONAYI_GEREKLI")) {
        setMultiPrompt({ message: msg.replace(/^.*ÇOKLU_PARTI_ONAYI_GEREKLI:\s*/, ""), payload });
      }
      /* aksi halde mutErr banner gösterir */
    }
  }

  const remaining = orderedQty != null && producedQty != null ? orderedQty - producedQty : null;
  // Yan ürün satırı grid'i: [ürün | oran | miktar+birim | sil] — başlık satırıyla birebir aynı.
  const bypGrid = "minmax(0, 1fr) 92px 118px 30px";
  const bypCountLocked = Math.floor(Number(count)) > 1;

  return (
    <>
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title="Parti Üretim Kaydı"
      subtitle={woCode ? `İş Emri: ${woCode}` : orderCode ? `Sipariş: ${orderCode}` : undefined}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={createMut.isPending}>İptal</button>
          <button type="submit" form="pb-form" className="btn-md primary" disabled={createMut.isPending || woUnassigned || resimEksik}>
            {createMut.isPending ? "Kaydediliyor…" : Math.floor(Number(count)) > 1 ? `${Math.floor(Number(count))} Parti Üret` : "Üretimi Kaydet"}
          </button>
        </>
      }
    >
      <form id="pb-form" onSubmit={submit} noValidate>
        {err || mutErr ? <div className="form-banner-err" role="alert">{err ?? mutErr}</div> : null}
        {/* ÖRNEK ÜRÜN RESMİ KAPISI — engel değil YÖNLENDİRME tonunda: kullanıcı bir şeyi bozmadı,
            bir adımı atlamış. Eşik ve eksik sayısı uçtan gelir. */}
        {resimEksik ? (
          <div className="form-banner-err" role="alert" style={{ background: "#fdf8ef", borderColor: "#e6c88a", color: "#7c4a08" }}>
            <b>Örnek ürün resmi eksik.</b> Bu iş emri için en az <b>{gateQ.data!.gerekli}</b> örnek ürün resmi yüklenmeden parti kaydı yapılamaz —
            şu an <b>{gateQ.data!.adet}</b> var, <b>{gateQ.data!.eksik}</b> tane daha gerekiyor.
            İş emri detayındaki <b>Ürün Resimleri</b> bölümünde kategoriyi <b>Örnek Ürün</b> seçip yükleyin.
          </div>
        ) : null}

        <div className="form-field">
          <label className="lbl" htmlFor="pb-item">Ürün</label>
          <div
            id="pb-item"
            style={{
              padding: "10px 12px",
              border: "1px solid var(--neutral-300)",
              borderRadius: "var(--radius-md)",
              background: "var(--neutral-50)",
              color: "var(--neutral-900)",
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            {itemLabel}
          </div>
          {orderedQty != null ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 18px", marginTop: 9 }}>
              <span style={{ fontSize: 12.5 }}>
                <span style={{ color: "var(--neutral-600)" }}>Sipariş </span>
                <b className="mono" style={{ color: "var(--neutral-900)", fontWeight: 700 }}>{fmtQty(orderedQty)}{uom ? ` ${uom}` : ""}</b>
              </span>
              {producedQty != null ? (
                <span style={{ fontSize: 12.5 }}>
                  <span style={{ color: "var(--neutral-600)" }}>Üretilen </span>
                  <b className="mono" style={{ color: producedQty > 0 ? "var(--success-text)" : "var(--neutral-700)", fontWeight: 700 }}>{fmtQty(producedQty)}</b>
                </span>
              ) : null}
              {remaining != null ? (
                <span style={{ fontSize: 12.5 }}>
                  <span style={{ color: "var(--neutral-600)" }}>Kalan </span>
                  <b className="mono" style={{ color: remaining <= 0 ? "var(--success-text)" : "var(--warning-text)", fontWeight: 700 }}>{fmtQty(Math.max(0, remaining))}</b>
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="form-field">
          <label className="lbl" htmlFor="pb-line">Üretim Hattı <span className="req">*</span></label>
          {woId ? (
            // WO-bağlı üretim: hat, iş emrinin ATANDIĞI hatlarla sınırlı (Hat Yönetimi'nden atanır).
            linesForWoQ.isLoading ? (
              <span className="help">İş emrinin hatları yükleniyor…</span>
            ) : woAssignedLines.length === 0 ? (
              <div className="help" style={{ color: "var(--warning-text)" }}>
                Bu iş emri bir üretim hattına atanmamış. Üretim girmek için önce <b>Hat Yönetimi</b>'nden bu iş emrini bir hatta atayın.
              </div>
            ) : woAssignedLines.length === 1 ? (
              // Tek hat → kilitli göster.
              <div className="form-input" style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--neutral-50)", cursor: "default" }} aria-readonly="true" title="İş emri bu hatta atanmış — üretim yalnız buraya girilir">
                <b className="mono">{woAssignedLines[0]!.code}</b>
                <span>{woAssignedLines[0]!.name}</span>
                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--success-text)", whiteSpace: "nowrap" }}>iş emrine atanmış hat</span>
              </div>
            ) : (
              // Çoklu hat → yalnız ATANMIŞ hatlar arasından seç.
              <>
                <select id="pb-line" className="form-select" value={locationId} onChange={(e) => setLocationId(e.target.value)} required>
                  {woAssignedLines.map((l) => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
                </select>
                <span className="help">Bu iş emri birden çok hatta atanmış — üretimin yapılacağı hattı seçin.</span>
              </>
            )
          ) : (
            <>
              <select id="pb-line" className="form-select" value={locationId} onChange={(e) => setLocationId(e.target.value)} required>
                <option value="">— Hat seç —</option>
                {lines.map((l) => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
              </select>
              {locationsQ.isSuccess && lines.length === 0 ? (
                <span className="help" style={{ color: "var(--warning-text)" }}>Aktif üretim hattı yok. Lokasyonlar'dan "üretim hattı" tipinde ekleyin.</span>
              ) : null}
            </>
          )}
        </div>

        <div className="form-field">
          <label className="lbl" htmlFor="pb-qty">Üretilen Miktar <span className="req">*</span></label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input id="pb-qty" className="form-input" type="number" step="any" min={0} value={qty} onChange={(e) => setQty(e.target.value)} inputMode="decimal" required style={{ flex: 1 }} />
            {uom ? <span className="mono" style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-700)", whiteSpace: "nowrap" }}>{uom}</span> : null}
          </div>
          <span className="help">Her parti bu miktarda; yeni lot olarak stoğa girer.</span>
        </div>

        <div className="form-field">
          <label className="lbl" htmlFor="pb-count">Parti Sayısı <span className="req">*</span></label>
          <input id="pb-count" className="form-input" type="number" step={1} min={1} max={100} value={count} onChange={(e) => setCount(e.target.value)} inputMode="numeric" required style={{ maxWidth: 140 }} />
          <span className="help">
            {(() => {
              const c = Math.floor(Number(count)); const q = Number(qty);
              if (c > 1 && q > 0) return `${c} parti × ${fmtQty(q)}${uom ? " " + uom : ""} = ${fmtQty(c * q)}${uom ? " " + uom : ""} toplam (her parti ayrı lot, FEFO ile ardışık tüketim).`;
              return "Aynı üründen kaç parti üretilecek. Her parti ayrı parti kaydı + ayrı lot oluşturur.";
            })()}
          </span>
        </div>

        {centerId && centerParams.length > 0 ? (
          <div style={{ borderTop: "1px solid var(--neutral-200)", margin: "4px 0 0", paddingTop: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--neutral-700)", marginBottom: 8 }}>
              Üretim Parametreleri
            </div>
            {centerParams.map((p) => {
              const val = paramValues[p.id] ?? "";
              const set = (v: string) => setParamValues((s) => ({ ...s, [p.id]: v }));
              const fid = `pb-param-${p.id}`;
              const labelEl = (
                <label className="lbl" htmlFor={fid}>
                  {p.label}{p.unit ? <span style={{ color: "var(--neutral-500)", fontWeight: 400 }}> ({p.unit})</span> : null}
                  {p.required ? <span className="req"> *</span> : null}
                </label>
              );
              if (p.dataType === "boolean") {
                return (
                  <div className="form-field" key={p.id}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, cursor: "pointer" }}>
                      <input id={fid} type="checkbox" checked={val === "true"} onChange={(e) => set(e.target.checked ? "true" : "")} />
                      <span>{p.label}{p.unit ? ` (${p.unit})` : ""}{p.required ? " *" : ""}</span>
                    </label>
                  </div>
                );
              }
              if (p.dataType === "select") {
                return (
                  <div className="form-field" key={p.id}>
                    {labelEl}
                    <select id={fid} className="form-select" value={val} onChange={(e) => set(e.target.value)}>
                      <option value="">— Seç —</option>
                      {p.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                );
              }
              if (p.dataType === "date") {
                return (
                  <div className="form-field" key={p.id}>
                    {labelEl}
                    <input id={fid} className="form-input" type="date" value={val} onChange={(e) => set(e.target.value)} />
                  </div>
                );
              }
              const isNum = p.dataType === "number";
              return (
                <div className="form-field" key={p.id}>
                  {labelEl}
                  <input
                    id={fid}
                    className="form-input"
                    type={isNum ? "number" : "text"}
                    step={isNum ? "any" : undefined}
                    inputMode={isNum ? "decimal" : undefined}
                    value={val}
                    onChange={(e) => set(e.target.value)}
                    maxLength={isNum ? undefined : 500}
                    placeholder={isNum && (p.minVal != null || p.maxVal != null) ? `${p.minVal ?? ""}…${p.maxVal ?? ""}` : undefined}
                  />
                </div>
              );
            })}
          </div>
        ) : null}

        {/* ═══ NE TÜKETİLECEK ═══
            Amaç yalnız bilgilendirme değil YANLIŞ YOLU KAPATMAK: kullanıcı burada hammadde
            listesi görüp "ben stok girişi yapacaktım" diyebilsin ve Mal Kabul'e yönelsin. */}
        {tuketimQ.data ? (
          <div className="pb-consume">
            {tuketimQ.data.hasRecipe ? (
              <>
                <div className="pb-consume-h">
                  <Icon name="alertTriangle" size={14} />
                  <span>Bu parti kaydedilince aşağıdaki bileşenler <b>stoktan düşülecek</b> (FEFO)</span>
                </div>
                <ul className="pb-consume-list">
                  {tuketimQ.data.items.map((it) => (
                    <li key={`${it.itemKind}:${it.itemId}`} className={it.short > 0 ? "eksik" : undefined}>
                      <span className="ad"><span className="mono">{it.code}</span> {it.name}</span>
                      <span className="mik mono">
                        {previewQty > 0
                          ? `${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(it.need)} ${it.uom}`
                          : `— ${it.uom}`}
                      </span>
                      {it.short > 0 && previewQty > 0 ? (
                        <span className="eks mono" title="Eldeki stok yetmiyor">
                          eksik {new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(it.short)}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
                <div className="pb-consume-alt">
                  Stok <b>girişi</b> mi yapıyorsunuz (sayım / mevcut stok)? Buradan değil,
                  <b> Stok / Lotlar → Mal Kabul → Giriş Nedeni: Sayım / Açılış stoğu</b> ile girin —
                  o yol reçete tüketmez.
                </div>
              </>
            ) : (
              <div className="pb-consume-h yok">
                <Icon name="info" size={14} />
                <span>Bu ürünün aktif reçetesi yok — <b>hiçbir bileşen düşülmeyecek</b>.</span>
              </div>
            )}
          </div>
        ) : null}

        {/* Yan Ürünler (co-production) — reçetede tanımlıysa otomatik dolar; operatör düzenler/ekler/kaldırır. */}
        <div style={{ borderTop: "1px solid var(--neutral-200)", margin: "8px 0 0", paddingTop: 14 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div className="lbl" style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Yan Ürünler</div>
              <div style={{ fontSize: 11.5, color: "var(--neutral-500)", lineHeight: 1.35 }}>
                Ana ürünle birlikte üretilenler — her biri kendi reçetesini tüketir, stoğa girer.
              </div>
            </div>
            <button
              type="button"
              className="btn-md secondary"
              style={{ height: 32, fontSize: 12.5, whiteSpace: "nowrap", flexShrink: 0 }}
              onClick={addByp}
              disabled={bypCountLocked}
              title={bypCountLocked ? "Yan ürün için Parti Sayısı 1 olmalı" : undefined}
            >
              <Icon name="plus" size={14} />Yan Ürün
            </button>
          </div>

          {bypCountLocked ? (
            <div className="help" style={{ color: "var(--warning-text)", marginTop: -2, marginBottom: 8 }}>
              Yan ürün eklemek için Parti Sayısı 1 olmalı.
            </div>
          ) : null}

          {byproducts.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{
                display: "grid", gridTemplateColumns: bypGrid, gap: 8, padding: "0 10px",
                fontSize: 10.5, fontWeight: 600, letterSpacing: "0.02em", textTransform: "uppercase", color: "var(--neutral-500)",
              }}>
                <span>Yan Ürün</span>
                <span title="Ana miktarın çarpanı (örn. 0,2 → ana×0,2)">Oran</span>
                <span>Miktar</span>
                <span aria-hidden="true" />
              </div>
              {byproducts.map((r) => {
                const u = bypUom(r.sel);
                return (
                  <div key={r.key} style={{
                    display: "grid", gridTemplateColumns: bypGrid, gap: 8, alignItems: "center", padding: 8,
                    border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)",
                  }}>
                    <SearchSelect value={r.sel || null} options={bypOptions} onChange={(v) => patchByp(r.key, { sel: v ?? "" })} placeholder="Ürün seç…" searchPlaceholder="Kod / ad ara…" emptyText="Üretilebilir ürün yok" />
                    <input
                      className="form-input" type="text" value={r.ratio}
                      onChange={(e) => setBypRatio(r.key, e.target.value.replace(/[^\d.,]/g, ""))}
                      placeholder="0,2" inputMode="decimal" aria-label="Oran (ana miktarın çarpanı)"
                      title="Ana miktarın çarpanı (örn. 0,2)" style={{ height: 34, fontSize: 12.5 }}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input
                        className="form-input" type="text" value={r.qty}
                        onChange={(e) => patchByp(r.key, { qty: e.target.value.replace(/[^\d.,]/g, ""), manual: true })}
                        placeholder="miktar" inputMode="decimal" aria-label="Üretilen miktar"
                        style={{ height: 34, fontSize: 12.5, minWidth: 0, flex: 1 }}
                      />
                      {u ? <span className="mono" style={{ fontSize: 11, color: "var(--neutral-500)", whiteSpace: "nowrap" }}>{u}</span> : null}
                    </div>
                    <button
                      type="button" onClick={() => setByproducts((rows) => rows.filter((x) => x.key !== r.key))}
                      aria-label="Yan ürünü kaldır" title="Kaldır"
                      style={{ height: 28, width: 28, border: 0, background: "transparent", color: "var(--danger)", cursor: "pointer", borderRadius: "var(--radius-sm)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                    ><Icon name="x" size={15} /></button>
                  </div>
                );
              })}
              <span className="help">Oran = ana miktarın çarpanı (örn. 0,2 → ana×0,2). Miktarı elle de girebilirsin; reçetede tanımlıysa otomatik dolar.</span>
            </div>
          ) : !bypCountLocked ? (
            <div style={{
              padding: "12px 14px", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)",
              background: "var(--neutral-50)", fontSize: 12, color: "var(--neutral-500)", textAlign: "center",
            }}>
              Yan ürün yok. Reçetede tanımlıysa otomatik gelir; <b style={{ fontWeight: 600, color: "var(--neutral-600)" }}>+ Yan Ürün</b> ile elle ekleyebilirsin.
            </div>
          ) : null}
        </div>

        <div className="form-field">
          <label className="lbl" htmlFor="pb-notes">Not</label>
          <textarea id="pb-notes" className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} rows={2} />
        </div>
      </form>
    </Drawer>
    <ProcessingOverlay open={createMut.isPending} title="Üretim kaydediliyor" message="Partiler ve otomatik yarı mamuller oluşturuluyor, lütfen bekleyin…" />
    <ConfirmDialog
      open={!!switchPrompt}
      title="Hattın iş emrini değiştir?"
      message={switchPrompt?.message ?? ""}
      tone="danger"
      confirmLabel="Onayla, Üret"
      cancelLabel="Vazgeç"
      busy={createMut.isPending}
      onConfirm={() => { if (switchPrompt) void runCreate({ ...switchPrompt.payload, confirmLineSwitch: true }); }}
      onCancel={() => setSwitchPrompt(null)}
    />
    <ConfirmDialog
      open={!!multiPrompt}
      title="Çoklu parti üretimi onayla"
      message={multiPrompt?.message ?? ""}
      tone="danger"
      confirmLabel="Onayla, Üret"
      cancelLabel="Vazgeç"
      busy={createMut.isPending}
      onConfirm={() => { if (multiPrompt) void runCreate({ ...multiPrompt.payload, confirmMultiBatch: true }); }}
      onCancel={() => setMultiPrompt(null)}
    />
    </>
  );
}
